import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DeliveredOrders from "@/pages/DeliveredOrders";
import PendingOrders from "@/pages/PendingOrders";
import AddSale from "@/pages/AddSale";
import SyncLog from "@/pages/SyncLog";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import InstallBanner from "@/components/InstallBanner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Sync Log, Settings, Add Sale, and Team & Roles are hidden from Sales Reps
// in the nav — this stops them reaching those pages by typing the URL
// directly too. Sales Reps are limited to Dashboard, Delivered Orders, and
// Pending Orders only.
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner" && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<Protected><Layout /></Protected>}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/delivered" element={<DeliveredOrders />} />
                <Route path="/pending" element={<PendingOrders />} />
                <Route path="/add-sale" element={<RequireAdmin><AddSale /></RequireAdmin>} />
                <Route path="/sync-log" element={<RequireAdmin><SyncLog /></RequireAdmin>} />
                <Route path="/settings" element={<RequireAdmin><Settings /></RequireAdmin>} />
                <Route path="/users" element={<RequireAdmin><Users /></RequireAdmin>} />
              </Route>
            </Routes>
            <Toaster position="top-right" richColors />
            <InstallBanner />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}